<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import TotpSetupForm from '../../components/js/Components/User/TotpSetupForm.vue'
import Alert from '../../components/js/Components/UI/Alert.vue'
import BlockContent from '../../components/js/Components/UI/Content/BlockContent.vue'
import MainContainer from '../../components/js/Components/UI/Content/MainContainer.vue'
import LazyContent from '../../components/js/Components/UI/Loading/LazyContent.vue'
import PlaceholderContent from '../../components/js/Components/UI/Loading/PlaceholderContent.vue'
import PageLayout from '../../components/js/Components/UI/PageLayout.vue'
import useLoading from '../../Composables/useLoading.ts'
import useTotpApi from '../../Composables/User/useTotpApi.ts'
import useUser from '../../Composables/User/useUser.ts'
import { AlertType } from '../../Constants/AlertType.ts'
import type { FormDef } from '../../Types/form'
import type { TotpSetup } from '../../Types/user'

const { t } = useI18n()
const { loading } = useLoading(true)
const { loading: sending } = useLoading(false)
const { getDef, setup } = useTotpApi()
const { user } = useUser()

const def = ref<FormDef | null>(null)
const data = ref<TotpSetup | null>(null)

async function fetchDef() {
  loading.value = true
  try {
    const { config, object } = await getDef()
    def.value = config
    data.value = object
  } finally {
    loading.value = false
  }
}

async function submit() {
  const code = data.value?.code ?? null
  if (null === code) {
    return
  }

  sending.value = true
  try {
    await setup(code)
    if (user.value !== null) {
      user.value.mustSetupTotp = false
      user.value.totpAuthenticationEnabled = true
    }
  } finally {
    sending.value = false
  }
}

onMounted(() => {
  fetchDef()
})
</script>

<template>
  <PageLayout>
    <MainContainer>
      <BlockContent :title="t('2fa.totp.setup.title')">
        <template v-if="user?.totpAuthenticationEnabled">
          <Alert :type="AlertType.SUCCESS" data-test="totp-setup-success-message">
            {{ t('2fa.totp.setup.successfully_configured') }}
          </Alert>
        </template>
        <template v-else>
          <div data-test="totp-setup-description">
            <h5>{{ t('2fa.totp.setup.description') }}</h5>

            <ul class="list-group list-group-flush">
              <li class="list-group-item">
                <span class="badge badge-primary badge-pill mr-2">1</span>
                {{ t('2fa.totp.setup.step_1_description') }}
              </li>
              <li class="list-group-item">
                <span class="badge badge-primary badge-pill mr-2">2</span>
                {{ t('2fa.totp.setup.step_2_description') }}
              </li>
            </ul>
          </div>

          <LazyContent :loading="loading">
            <TotpSetupForm
              v-if="data && def"
              :setup="data"
              :def="def"
              :sending="sending"
              data-test="totp-setup-form"
              @submit="submit"
            />

            <template #loader>
              <PlaceholderContent :rows="4" />
            </template>
          </LazyContent>
        </template>
      </BlockContent>
    </MainContainer>
  </PageLayout>
</template>
